import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import Swal from "sweetalert2";
import api from "../../../../api/api";
import ReceiptGenerationPDF from "./ReceiptGenerationPDF";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PaymentIcon from "@mui/icons-material/Payment";

const CarAgreementReceipt = () => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const [receipts, setReceipts] = useState([]);
  const [agreements, setAgreements] = useState([]);

  const [open, setOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const [selectedAgreement, setSelectedAgreement] = useState("");
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);

  const [paymentAmount, setPaymentAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(8);

  // ================= FETCH =================
  useEffect(() => {
    fetchReceipts();
    fetchAgreements();
  }, []);

  const fetchReceipts = async () => {
    try {
      const res = await api.get("/car-receipts/");
      setReceipts(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchAgreements = async () => {
    try {
      const res = await api.get("/car-agreements/");
      setAgreements(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  // ================= CREATE RECEIPT =================
  const handleCreateReceipt = async () => {
    if (!selectedAgreement) {
      Swal.fire("Error", "Select agreement", "error");
      return;
    }

    try {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 2000));

      const res = await api.post("/car-receipts/", {
        agreement: selectedAgreement,
      });

      setReceipts((prev) => [res.data, ...prev]);

      Swal.fire("Success", "Receipt created", "success");
      setOpen(false);
      setSelectedAgreement("");
    } catch (err) {
      console.log(err.response?.data);
      Swal.fire("Error", "Failed to create receipt", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= APPLY PAYMENT =================
  const handleApplyPayment = async () => {
    if (!paymentAmount) {
      Swal.fire("Error", "Enter amount", "error");
      return;
    }

    if (!selectedReceiptId) {
      Swal.fire("Error", "No receipt selected", "error");
      return;
    }

    try {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 1500));

      await api.post(
        `/car-receipts/${selectedReceiptId}/apply_payment/`,
        {
          amount: paymentAmount,
        }
      );

      Swal.fire("Success", "Payment applied", "success");

      setPaymentOpen(false);
      setPaymentAmount("");
      setSelectedReceiptId(null);

      fetchReceipts();
    } catch (err) {
      console.log(err.response?.data);
      Swal.fire("Error", "Payment failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={isMobile ? 1 : 3}>
      <Paper sx={{ p: 3 }}>

        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5">
            Car Purchase Receipts
          </Typography>

          <Button variant="contained" onClick={() => setOpen(true)}>
            Create Receipt
          </Button>
        </Box>

        {/* TABLE */}
        <TableContainer>
          <Table>

            <TableHead>
              <TableRow>
                <TableCell>Buyer</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {receipts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((r) => (
                  <TableRow key={r.id}>

                    <TableCell>{r.buyer_name}</TableCell>

                    <TableCell>
                      {r.make} {r.model}
                    </TableCell>

                    <TableCell>${r.total_amount}</TableCell>
                    <TableCell>${r.total_paid}</TableCell>
                    <TableCell>${r.new_balance}</TableCell>

                    <TableCell>
                      {r.is_fully_paid ? "Completed" : "Pending"}
                    </TableCell>

                    <TableCell>
                      <Box display="flex" gap={1}>

                        <Button
                          variant="outlined"
                          startIcon={<PaymentIcon />}
                          disabled={r.is_fully_paid}
                          onClick={() => {
                            setSelectedReceiptId(r.id);
                            setPaymentOpen(true);
                          }}
                        >
                          Continue
                        </Button>

                        <PDFDownloadLink
                          document={<ReceiptGenerationPDF receipt={r} />}
                          fileName={`receipt-${r.id}.pdf`}
                        >
                          {({ loading }) => (
                            <Button variant="contained">
                              {loading ? "..." : "PDF"}
                            </Button>
                          )}
                        </PDFDownloadLink>

                      </Box>
                    </TableCell>

                  </TableRow>
                ))}
            </TableBody>

          </Table>

          {receipts.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={receipts.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[8]}
            />
          )}

        </TableContainer>
      </Paper>

      {/* CREATE DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create Receipt</DialogTitle>

        <DialogContent>
          <TextField
            select
            fullWidth
            SelectProps={{ native: true }}
            value={selectedAgreement}
            onChange={(e) => setSelectedAgreement(e.target.value)}
          >
            <option value=""></option>
            {agreements.map((a) => (
              <option key={a.id} value={a.id}>
                {a.buyer_name}
              </option>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateReceipt}>
            {loading ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PAYMENT DIALOG */}
      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)}>
        <DialogTitle>Continue Payment</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
          <Button onClick={handleApplyPayment}>
            {loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CarAgreementReceipt;


// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TablePagination,
//   TableRow,
//   TextField,
//   Typography,
//   useMediaQuery,
// } from "@mui/material";

// import Swal from "sweetalert2";
// import api from "../../../../api/api";
// import ReceiptGenerationPDF from "./ReceiptGenerationPDF";
// import { PDFDownloadLink } from "@react-pdf/renderer";

// import PaymentIcon from "@mui/icons-material/Payment";

// const CarAgreementReceipt = () => {
//   const isMobile = useMediaQuery("(max-width:768px)");

//   const [receipts, setReceipts] = useState([]);
//   const [agreements, setAgreements] = useState([]);

//   const [open, setOpen] = useState(false);
//   const [paymentOpen, setPaymentOpen] = useState(false);

//   const [selectedAgreement, setSelectedAgreement] = useState("");
//   const [selectedAgreementId, setSelectedAgreementId] = useState(null);

//   const [paymentAmount, setPaymentAmount] = useState("");
//   const [loading, setLoading] = useState(false);

//   const [page, setPage] = useState(0);
//   const [rowsPerPage] = useState(8);

//   // ================= FETCH =================
//   useEffect(() => {
//     fetchReceipts();
//     fetchAgreements();
//   }, []);

//   const fetchReceipts = async () => {
//     const res = await api.get("/car-receipts/");
//     setReceipts(res.data);
//   };

//   const fetchAgreements = async () => {
//     const res = await api.get("/car-agreements/");
//     setAgreements(res.data);
//   };

//   // ================= CREATE RECEIPT =================
//   const handleCreateReceipt = async () => {
//     if (!selectedAgreement) {
//       Swal.fire("Error", "Select agreement", "error");
//       return;
//     }

//     try {
//       setLoading(true);
//       await new Promise((r) => setTimeout(r, 2000));

//       const res = await api.post("/car-receipts/", {
//         agreement: selectedAgreement,
//       });

//       setReceipts((prev) => [res.data, ...prev]);

//       Swal.fire("Success", "Receipt created", "success");
//       setOpen(false);
//     } catch (err) {
//       console.log(err.response?.data);
//       Swal.fire("Error", "Failed to create receipt", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= APPLY PAYMENT =================
//  const handleApplyPayment = async (receiptId) => {
//   if (!paymentAmount) {
//     Swal.fire("Error", "Enter amount", "error");
//     return;
//   }

//   try {
//     setLoading(true);
//     await new Promise((r) => setTimeout(r, 2000));

//     await api.post(
//       `/car-receipts/${receiptId}/apply_payment/`,
//       {
//         amount: paymentAmount,
//       }
//     );

//     Swal.fire("Success", "Payment applied", "success");

//     setPaymentOpen(false);
//     setPaymentAmount("");

//     fetchReceipts();

//   } catch (err) {
//     console.log(err.response?.data);
//     Swal.fire("Error", "Payment failed", "error");
//   } finally {
//     setLoading(false);
//   }
// };

//   return (
//     <Box p={isMobile ? 1 : 3}>

//       <Paper sx={{ p: 3 }}>

//         {/* HEADER */}
//         <Box display="flex" justifyContent="space-between" mb={2}>
//           <Typography variant="h5">
//             Car Purchase Receipts
//           </Typography>

//           <Button onClick={() => handleApplyPayment(selectedAgreementId)}>
//             Create Receipt
//           </Button>
//         </Box>

//         {/* TABLE */}
//         <TableContainer>
//           <Table>

//             <TableHead>
//               <TableRow>
//                 <TableCell>Buyer</TableCell>
//                 <TableCell>Vehicle</TableCell>
//                 <TableCell>Total</TableCell>
//                 <TableCell>Paid</TableCell>
//                 <TableCell>Balance</TableCell>
//                 <TableCell>Status</TableCell>
//                 <TableCell>Actions</TableCell>
//               </TableRow>
//             </TableHead>

//             <TableBody>
//               {receipts
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((r) => (
//                   <TableRow key={r.id}>

//                     <TableCell>{r.buyer_name}</TableCell>

//                     <TableCell>
//                       {r.make} {r.model}
//                     </TableCell>

//                     <TableCell>${r.total_amount}</TableCell>

//                     <TableCell>${r.total_paid}</TableCell>

//                     <TableCell>${r.new_balance}</TableCell>

//                     <TableCell>
//                       {r.is_fully_paid ? "Completed" : "Pending"}
//                     </TableCell>

//                     <TableCell>
//                       <Box display="flex" gap={1}>

//                         <Button
//                           variant="outlined"
//                           startIcon={<PaymentIcon />}
//                           disabled={r.is_fully_paid}
//                           onClick={() => {
//                           setSelectedAgreementId(r.id);
//                           setPaymentOpen(true);
//                           }}
//                         >
//                           Continue
//                         </Button>

//                         <PDFDownloadLink
//                           document={<ReceiptGenerationPDF receipt={r} />}
//                           fileName={`receipt-${r.id}.pdf`}
//                         >
//                           {({ loading }) => (
//                             <Button variant="contained">
//                               {loading ? "..." : "PDF"}
//                             </Button>
//                           )}
//                         </PDFDownloadLink>

//                       </Box>
//                     </TableCell>

//                   </TableRow>
//                 ))}
//             </TableBody>

//           </Table>

//           {receipts.length > rowsPerPage && (
//             <TablePagination
//               component="div"
//               count={receipts.length}
//               page={page}
//               onPageChange={(e, newPage) => setPage(newPage)}
//               rowsPerPage={rowsPerPage}
//               rowsPerPageOptions={[8]}
//             />
//           )}

//         </TableContainer>
//       </Paper>

//       {/* CREATE DIALOG */}
//       <Dialog open={open} onClose={() => setOpen(false)}>
//         <DialogTitle>Create Receipt</DialogTitle>

//         <DialogContent>
//           <TextField
//             select
//             fullWidth
//             SelectProps={{ native: true }}
//             value={selectedAgreement}
//             onChange={(e) => setSelectedAgreement(e.target.value)}
//           >
//             <option value=""></option>
//             {agreements.map((a) => (
//               <option key={a.id} value={a.id}>
//                 {a.buyer_name}
//               </option>
//             ))}
//           </TextField>
//         </DialogContent>

//         <DialogActions>
//           <Button onClick={() => setOpen(false)}>Cancel</Button>

//           <Button onClick={handleCreateReceipt}>
//             {loading ? <CircularProgress size={20} /> : "Create"}
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {/* PAYMENT DIALOG */}
//       <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)}>
//         <DialogTitle>Continue Payment</DialogTitle>

//         <DialogContent>
//           <TextField
//             fullWidth
//             type="number"
//             label="Amount"
//             value={paymentAmount}
//             onChange={(e) => setPaymentAmount(e.target.value)}
//           />
//         </DialogContent>

//         <DialogActions>
//           <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>

//           <Button onClick={handleApplyPayment}>
//             {loading ? <CircularProgress size={20} /> : "Submit"}
//           </Button>
//         </DialogActions>
//       </Dialog>

//     </Box>
//   );
// };

// export default CarAgreementReceipt;

